import type { SubscriptionLapseInfo } from '@/types/subscription';

export type PaywallFeatureConfig = {
  enabled: boolean;
  requiresSubscription: boolean;
  requiredPlanSlugs?: string[];
  title?: string;
  description?: string;
};

const DEFAULT_PAYWALL_TITLE = 'Go on offense. Talk to the person who hires.';
const DEFAULT_PAYWALL_DESCRIPTION =
  'Find the decision-maker, write from your own inbox, track every reply. Stop applying. Start landing.';

const FEATURE_NAME_OVERRIDES: Record<string, string> = {
  'contacts-lookup': 'Contacts Lookup',
  'hub-interviews': 'Interview Tracking',
  reports: 'Company Reports',
  'resume-analysis': 'Resume Analysis',
  'outreach-send': 'Outreach Sending',
};

export const PAYWALL_CONFIG = {
  enabled: true,
  tokenCookieName: 'paywall_status',
  tokenTtlSeconds: 15 * 60,
};

export const PAYWALL_FEATURES: Record<string, PaywallFeatureConfig> = {
  default: {
    enabled: true,
    requiresSubscription: true,
    title: DEFAULT_PAYWALL_TITLE,
    description: DEFAULT_PAYWALL_DESCRIPTION,
  },
  reports: {
    enabled: true,
    requiresSubscription: true,
    title: '25 companies that should be hiring you — and the people who make those calls.',
    description:
      'Job boards put you in a stack of 500. We map the companies that fit your background, surface the decision-makers, and hand you their direct emails.',
  },
  'contacts-lookup': {
    enabled: true,
    requiresSubscription: true,
    title: 'Skip the application portal. Email the person who hires.',
    description:
      'Direct emails for the people who actually decide — sourced from real LinkedIn profiles, role signals, and company structure. ATS doesn\'t read these inboxes. They do.',
  },
  'hub-interviews': {
    enabled: true,
    requiresSubscription: true,
    title: 'Run every interview as a process you control.',
    description:
      'Prep, notes, and outcomes per company in one place. Walk in informed, follow up sharp, and never lose track of where you stand.',
  },
  'resume-analysis': {
    enabled: true,
    requiresSubscription: true,
    title: 'See exactly what\'s costing you replies — section by section.',
    description:
      'A real review of your resume against the role. You get the score, the weak points, and a fix path you can apply in place.',
  },
  'outreach-send': {
    enabled: true,
    requiresSubscription: true,
    title: 'Send it from your own inbox — straight to the person who hires.',
    description:
      'Approve and send your outreach from your connected Gmail, with live deliverability checks and every reply tracked. Skip the application portal and land in the inbox that decides.',
  },
};

export const FREE_PLAN = {
  slug: 'free',
  name: 'Research',
  description: 'Access jobs, companies, and contacts. Run your own search with insider data.',
  billing_price_cents: 0,
  billing_currency: 'USD',
  billing_type: 'FREE',
  is_free: true,
  cta_href: '/get-started',
  highlights: [
    'Job search across 8M+ listings',
    'Company profiles and research',
    'People and contact search',
    'Resume builder and improvements',
    'Outreach message generator',
  ],
};

export const PLAN_MARKETING_OVERRIDES: Record<
  string,
  {
    name?: string;
    description?: string;
    billing_price_cents?: number | null;
    highlights?: string[];
  }
> = {
  'pro-yearly': {
    name: 'Guided Launch',
    description: 'Launch your campaign with a human beside you — everything in Campaign, plus hands-on strategy for a full year.',
    billing_price_cents: 14900,
    highlights: [
      'Founder strategy call with Kevin',
      'Campaign setup, targeting, and optimization',
      'Priority support and priority email to the founder',
      'Post-placement protection (12 months)',
      'Includes everything in Campaign',
      'Human-assisted contact research from LinkedIn profiles, role signals, and company context',
      'Smart resume analysis',
      'Unlimited contact discovery and contacts reports*',
      'Unlimited outreach workflows and message drafting*',
      'Success spotlight when you land your role',
      'Save 38% vs monthly billing',
    ],
  },
  'pro-monthly': {
    name: 'Campaign',
    description: 'Turn on auto-apply and auto-outreach, and let a human-guided system keep your search moving every day.',
    billing_price_cents: 2000,
    highlights: [
      'Everything in Research',
      'Auto-apply to matched roles — up to 10/day',
      'Auto-outreach to hiring managers — up to 5/day',
      'Approval queue — review every match before it goes out',
      'Gmail integration · sends outreach as you',
      'Follow-up drafts for unanswered outreach',
      'Daily caps you control',
      'Smart resume analysis',
      'Unlimited contact discovery and contacts reports*',
    ],
  },
};

export function getPaywallFeatureConfig(featureSlug?: string): PaywallFeatureConfig {
  if (!featureSlug) {
    return PAYWALL_FEATURES.default;
  }
  return PAYWALL_FEATURES[featureSlug] || PAYWALL_FEATURES.default;
}

export function getPaywallFeatureDisplayName(featureSlug?: string): string | undefined {
  if (!featureSlug) return undefined;
  if (FEATURE_NAME_OVERRIDES[featureSlug]) return FEATURE_NAME_OVERRIDES[featureSlug];

  const cleaned = featureSlug
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return undefined;

  return cleaned
    .split(' ')
    .map((word) => (word.length > 0 ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
}

export function resolvePaywallDialogCopy({
  featureSlug,
  title,
  description,
}: {
  featureSlug?: string;
  title?: string;
  description?: string;
}) {
  const featureConfig = getPaywallFeatureConfig(featureSlug);
  const featureName = getPaywallFeatureDisplayName(featureSlug);

  const resolvedTitle =
    title ??
    featureConfig.title ??
    (featureName ? `${featureName} runs on a paid plan.` : DEFAULT_PAYWALL_TITLE);

  const resolvedDescription =
    description ??
    featureConfig.description ??
    (featureName
      ? `Pick a plan to use ${featureName.toLowerCase()} and run a real outreach offensive.`
      : DEFAULT_PAYWALL_DESCRIPTION);

  return {
    featureName,
    title: resolvedTitle,
    description: resolvedDescription,
  };
}

export function getPaywallReadMoreCopy(featureSlug?: string, title?: string) {
  const { featureName } = resolvePaywallDialogCopy({ featureSlug, title });
  if (!featureName) {
    return {
      label: 'How it works',
      description: 'A short walkthrough of what this unlocks.',
    };
  }
  return {
    label: `How ${featureName} works`,
    description: `What ${featureName} actually does, and how to use it.`,
  };
}

export function isPaywallEnabled(featureSlug?: string): boolean {
  const featureConfig = getPaywallFeatureConfig(featureSlug);
  return PAYWALL_CONFIG.enabled && featureConfig.enabled;
}

export function featureRequiresSubscription(featureSlug?: string): boolean {
  const featureConfig = getPaywallFeatureConfig(featureSlug);
  return featureConfig.requiresSubscription;
}

export function checkFeatureAccess(
  featureSlug: string | undefined,
  subscriptionData: {
    hasPremium?: boolean;
    planSlug?: string | null;
    status?: string | null;
  } | undefined
): boolean {
  const featureConfig = getPaywallFeatureConfig(featureSlug);

  if (!PAYWALL_CONFIG.enabled || !featureConfig.enabled || !featureConfig.requiresSubscription) {
    return true;
  }

  const isActive =
    subscriptionData?.hasPremium ||
    ['ACTIVE', 'TRIALING'].includes(subscriptionData?.status?.toUpperCase?.() || '');

  if (!isActive) {
    return false;
  }

  if (featureConfig.requiredPlanSlugs && featureConfig.requiredPlanSlugs.length > 0) {
    return featureConfig.requiredPlanSlugs.includes(subscriptionData?.planSlug || '');
  }

  return true;
}

/**
 * Resolve dialog copy for a lapsed-subscription user. Adds a personalized
 * banner ("Your Campaign plan expired on April 1") on top of the
 * standard feature copy. Returns null when nothing personalized should appear
 * (NEVER_SUBSCRIBED — the standard feature copy is enough).
 */
export interface PaywallLapseCopy {
  banner: string;
  cta: string;
  /** When true, the dialog should expose a "View existing reports" dismiss option. */
  allowDismiss: boolean;
}

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export function resolvePaywallLapseCopy(
  lapseInfo: SubscriptionLapseInfo | null | undefined,
  options?: { allowDismiss?: boolean },
): PaywallLapseCopy | null {
  if (!lapseInfo) return null;

  const allowDismiss = options?.allowDismiss ?? false;
  const planName = lapseInfo.previousPlanName ?? 'subscription';
  const expiredAt = formatDate(lapseInfo.expiredAt);

  switch (lapseInfo.lapseType) {
    case 'NEVER_SUBSCRIBED':
      return null;

    case 'EXPIRED':
      return {
        banner: expiredAt
          ? `Your ${planName} plan expired on ${expiredAt}. Renew to keep finding contacts and running outreach.`
          : `Your ${planName} plan expired. Renew to keep finding contacts and running outreach.`,
        cta: 'Renew subscription',
        allowDismiss,
      };

    case 'CANCELED':
      return {
        banner: expiredAt
          ? `Your ${planName} plan was canceled and ended on ${expiredAt}. Reactivate to continue.`
          : `Your ${planName} plan was canceled. Reactivate to continue.`,
        cta: 'Reactivate subscription',
        allowDismiss,
      };

    case 'PAST_DUE':
      return {
        banner: `Your last payment for ${planName} didn't go through. Update your payment method to restore access.`,
        cta: 'Update payment',
        allowDismiss,
      };

    case 'INCOMPLETE':
      return {
        banner: `Your ${planName} checkout wasn't completed. Pick up where you left off.`,
        cta: 'Finish checkout',
        allowDismiss,
      };

    default:
      return null;
  }
}
